import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCarbonArcConnectionStatus } from '@/services/integrations/carbon-arc';
import {
  CARBON_ARC_PROVIDER,
  subscribeToConnectResults,
} from '@/utils/oauth-connect-signal';
import { useOrgStore } from '@/stores/org-store';

export const CARBON_ARC_CONNECTION_QUERY_KEY = 'carbon-arc-connection';

export const carbonArcConnectionQueryKey = (orgId: string | undefined) =>
  [CARBON_ARC_CONNECTION_QUERY_KEY, orgId] as const;

export const useCarbonArcConnection = ({ enabled }: { enabled: boolean }) => {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const queryClient = useQueryClient();
  const isAsking = enabled && !!orgId;

  useEffect(() => {
    // Consent completing is the only event that changes this answer, and the
    // popup keeps the canvas focused throughout, so no focus or reconnect
    // event would report it. Nothing to re-check while nothing is asked, so
    // the listener is not opened either.
    if (!isAsking) return;
    return subscribeToConnectResults(({ provider, connected }) => {
      if (provider !== CARBON_ARC_PROVIDER || !connected) return;
      // Invalidated rather than written: the reported outcome comes from the
      // browser, and only the token row decides whether a run can proceed.
      queryClient.invalidateQueries({
        queryKey: carbonArcConnectionQueryKey(orgId),
      });
    });
  }, [isAsking, queryClient, orgId]);

  return useQuery<boolean | null, Error>({
    queryKey: carbonArcConnectionQueryKey(orgId),
    queryFn: () => getCarbonArcConnectionStatus(orgId!),
    enabled: isAsking,
    staleTime: 5 * 60_000,
    // A failed check resolves to null, which shows nothing. Retrying would
    // repeat a request whose failure has already been handled.
    retry: false,
  });
};
